import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { submitPayoutsStep } from '@/modules/onboarding/actions/submitPayoutsStep';
import { updatePartnerType } from '@/modules/onboarding/actions/updatePartnerType';
import type { TFsmEvent } from '@/modules/onboarding/fsm-types';
import { PayoutsForm, mapPhoLicenseToForm } from '@/modules/onboarding/model/payoutsStep';
import { failedStepResult, isStepAccepted } from '@/modules/onboarding/model/stepErrors';
import { loadPartnerDocDefinitions } from '@/modules/partner/actions/async/loadDocDefinitions';
import { prepareUploadPartnerForm } from '@/modules/partner/actions/async/preparePartnerUploadForm';
import { IPartner } from '@/modules/partner/model';
import { PartnerDocType } from '@/modules/partner/model/partner-doc-type';
import { RUPartnerType, SELF_EMPLOYED, TPartnerType } from '@/modules/partner/model/partnerType';
import { getPartner } from '@/modules/partner/store/selectors';
import { partnerUploadSelectors } from '@/modules/partner/store/uploadSlice';
import type { IFormProps } from '@/pages/Wizard/view-types';
import { AppDispatch } from '@/root-store';
import { eventTracking } from '@/services/event-tracking';
import { useUpdateEffect } from '@/shared/hooks/useUpdateEffect';
import { CountryCode } from '@/shared/models/country-code';
import { useForm } from '@/shared/view/Form';
import { HandleDocListItemClick } from '@src-common/view/components/DocsList';

import { PaymentTypeView } from './view';

type Props = IFormProps<TFsmEvent>;

const getInitialPartnerName = (partnerType: TPartnerType | undefined, partner: IPartner) => {
  if (partnerType === RUPartnerType.ip) {
    return `ИП ${[partner.lastName, partner.firstName, partner.middleName]
      .filter(Boolean)
      .join(' ')}`;
  }

  return '';
};

const getInitialPartnerType = (partner: IPartner) => {
  if (partner.partnerType) {
    return partner.partnerType;
  }

  if (partner.countryCode === CountryCode.Russia) {
    return RUPartnerType.ip;
  }

  return SELF_EMPLOYED;
};

const mapPartnerToForm = (partner: IPartner): PayoutsForm => {
  const { legalName, vatRegNo, companyRegNumber, legalAddress1, inn, legalKpp, taxProfile } =
    partner;

  const partnerType = getInitialPartnerType(partner);

  return {
    partnerType,
    companyName: legalName || getInitialPartnerName(partnerType, partner),
    companyAddress: legalAddress1,
    vatRegNo,
    companyRegNumber,
    inn,
    kpp: legalKpp,

    // US onboarding fields
    ssn: partner.ssn ?? '',
    dbaName: partner.dbaName ?? '',
    zipCode: partner.legalRegisteredAddress?.zipCode ?? '',
    city: partner.legalRegisteredAddress?.city ?? '',
    licenseNumber: partner.licenseNumber || '',
    ein: partner.ein ?? '',

    // GB onboarding fields
    taxProfile,
    vatAgreementAccepted: false,
    hasPhoLicense: mapPhoLicenseToForm(partner.hasPhoLicense),
  };
};

const PaymentType: React.FC<Props> = ({ onSwitchScreen }) => {
  const dispatch = useDispatch() as AppDispatch;

  const handleClickDocListItem = useCallback<HandleDocListItemClick>(
    (event, doc) =>
      dispatch(
        prepareUploadPartnerForm({
          docType: doc.docType as PartnerDocType,
          onSuccess: () => onSwitchScreen('UPLOAD'),
        }),
      ),
    [dispatch, onSwitchScreen],
  );

  const partner = useSelector(getPartner);

  const postForm = useCallback(
    async (formValues: Parameters<typeof submitPayoutsStep>[0]['formValues']) => {
      const values = { formValues, countryCode: partner.countryCode };
      const resultAction = await dispatch(submitPayoutsStep(values));

      if (submitPayoutsStep.fulfilled.match(resultAction)) {
        const { isSucceed, result = {} } = resultAction.payload;

        if (isSucceed && isStepAccepted(result)) {
          eventTracking.trackSnowplow({
            event: 'Onboarding - Payouts - Success',
            type: formValues.partnerType,
          });

          onSwitchScreen('NEXT');
        }

        return result;
      }

      return failedStepResult(resultAction.error);
    },
    [dispatch, onSwitchScreen, partner],
  );

  const {
    handleSubmit,
    form: {
      setValue,
      control,
      watch,
      formState: { isSubmitting },
    },
  } = useForm({
    preSaveStore: {
      storeId: `payoutsStepForm-${partner.id}`,
      type: 'memory',
    },
    onSubmit: postForm,
    defaultValues: mapPartnerToForm(partner),
  });

  const partnerType = watch('partnerType');

  useEffect(() => {
    dispatch(loadPartnerDocDefinitions(partnerType));

    eventTracking.trackSnowplow({
      event: 'Onboarding - Payouts - Switch',
      type: partnerType,
    });
  }, [dispatch, partnerType]);

  useUpdateEffect(() => {
    setValue('companyName', getInitialPartnerName(partnerType, partner));

    dispatch(updatePartnerType(partnerType));
  }, [dispatch, partnerType]);

  return (
    <form onSubmit={handleSubmit}>
      <PaymentTypeView
        isSubmitting={isSubmitting}
        docs={useSelector(partnerUploadSelectors.getDocsList)}
        onDocListItemClick={handleClickDocListItem}
        control={control}
      />
    </form>
  );
};

export { PaymentType };
